import * as React from 'react';

import { IconName } from 'flatiron/lib';

import { ProcessStage } from 'Src/generated/graphql';

import { ToastContextValue } from '../../app/components/ToastManager/ToastContext';

declare global {
  interface Window {
    INTURN_ENV: {
      IUI_HOST: string;
      IUI_PORT: number;
      IUI_CORE_API_DOMAIN: string;
      IUI_AUTH_DOMAIN: string;
      IUI_WS_HOSTNAME: string;
      IUI_WS_PORT: number;
      IUI_GA_TRACKING_CODE: string;
      IUI_NEW_RELIC_APP_ID: string;
      IUI_RECAPTCHA_SITE_KEY: string;
      DEPLOY_ENV: string;
      IUI_DOCUMENT_SERVICE_DOMAIN: string;
      IUI_GRAPHQL_DOMAIN: string;
      GTM_CONTAINER_ID?: string;
    };
    dataLayer: any[];
  }
}

export type Dispatch = (action: { type: string; payload: any }) => void;

export type History = {
  push: (url: string) => void;
  replace: (url: string) => void;
  location: Location;
};

export type MatchPath = (path: string, args: { path: string }) => boolean;

export type State = {
  getIn: (path: string[]) => any;
};

export type Location = {
  pathname: string;
  query: { [key: string]: string };
  search: string;
};

export type CurrencyConversion = {
  displayCurrency: string;
  isActive: boolean;
};

export type ToastNotification = {
  dismiss: () => void;
};

export type Notify = ToastContextValue['toast'];

export type Activity = {
  subscribe: (
    entity: string,
    eventName: string,
    callback: (e: any) => void,
  ) => string;
  unsubscribeById: (entity: string, eventName: string, id: string) => void;
};

export type ElfQuery = {
  // props
  operation: string;
  src: any[];
  ids: any[];
  skipLoad: boolean;
  skipDestroy: boolean;
  isOptimistic: boolean;
  queuePriority: number;
  infiniteScroll: number;
  isFetchingNextPage: boolean;

  // methods
  constructor: (state: {}, run: any) => {};
  setState: (nextState: {}) => ElfQuery;
  merge: (key: string, value: any) => ElfQuery;
  get: (key: string) => ElfQuery;
  unshift: (key: string, value: any) => ElfQuery;
  push: (key: string, value: any) => ElfQuery;
  toJS: () => {};
  hashCode: () => string;
  genericHashCode: () => number;
  options: (opts: {}) => ElfQuery;
  ttl: (n: any) => ElfQuery;
  queue: (type: string | boolean, ms?: number) => ElfQuery;
  forceRefresh: (state: any) => any;
  invalidateCache: () => any;
  onDispatch: (cb: any) => ElfQuery;
  onError: (cb: any) => ElfQuery;
  onSuccess: (cb: any) => ElfQuery;
  onMutate: (cb: any) => ElfQuery;
  type: (type: any) => ElfQuery;
  setOperation: (operation: string) => ElfQuery;
  prependToSrc: (value: string) => any;
  id: (val: any) => ElfQuery;
  in: (a: any, b?: any) => ElfQuery;
  params: (p: any) => ElfQuery;
  body: (b: any) => ElfQuery;
  optimisticUpdateBody: (b: any) => any;
  find: (a: any) => ElfQuery;
  update: (a: any) => ElfQuery;
  create: (a: any) => ElfQuery;
  destroy: (a: any) => ElfQuery;
  run: (a: any) => any;
};

export type Elf = {
  of: (a: any) => ElfQuery;
  find: (a: any, id?: any) => ElfQuery;
  update: (a: any) => ElfQuery;
  create: (a: any) => ElfQuery;
  destroy: (a: any) => ElfQuery;
  operation: (a: any) => ElfQuery;
  new: (a: any) => ElfQuery;
  empty: (a: any) => ElfQuery;
};

export type WorkflowUpdatedStage = ProcessStage;

export type ActivityProcessEvent = {
  data: {
    event: string;
    state: string;
    stages?: WorkflowUpdatedStage[];
  };
};

export type Match = {
  params: {
    [key: string]: string | void;
  };
  isExact: boolean;
  path: string;
  url: string;
};

export type RouteProps = {
  history: History;
  notify: Notify;
  match: Match;
};

export type OfferPermissions = {
  canAddAndRemoveItems: boolean;
  canEditOffer: boolean;
  canEditSizes: boolean;
  canEditPrices: boolean;
  canEditQuantity: boolean;
  isTakeAllOffer: boolean;
};

export type Enhance<Props, EnhancedProps> = (
  Component: React.ComponentType<EnhancedProps>,
) => React.ComponentType<Props>;

export type I18n = {
  t: (k: string) => string;
};

export type KeyListenerStream = {
  data: Map<string, any>;
  subscribe: (pattern: string, next: (a: any) => void) => any;
  unsubscribe: (listener: any) => void;
};

export type Process = {
  upsert: (arg: Notification) => void;
  remove: (arg: Notification['id']) => void;
};

export type QueryString = {
  stringify: (a: any) => string;
};

export type Store = {
  dispatch: Dispatch;
};

export type StoreSubscription = {};

export type Messenger = {
  open: (id: string | null) => void;
  close: () => void;
};

// This is the massive object of props that gets passed into top level
// components by <Root />
export type RootProps = {
  activity: Activity;
  dispatch: Dispatch;
  keyListenerStream: KeyListenerStream;
  location: Location;
  match: Match;
  matchPath: MatchPath;
  messenger: Messenger;
  notificationVerticalOffset: number;
  notifications: Notification[];
  notify: Notify;
  process: Process;
  qs: QueryString;
  storeSubscription: StoreSubscription;
  i18n: {
    t: (arg: string) => string;
  };
  isBulkMove?: boolean;
  activeTabIndex?: number; // to remove isBulkMove & activeTabIndex later
};

export type ProductEntity = 'casepack' | 'item';
export type ProductEntityPlural = 'casepacks' | 'items';
export type ProductEntityShorthand = 'CSPK' | 'Item';
export type ProductEntityShorthandPlural =
  | 'CSPKs'
  | 'Items'
  | 'Products'
  | 'Offers'
  | 'Unallocated';
export type LegacyProductEntityPlural = 'casepacks' | 'products';

export type AppStream = {
  subscribe: (event: string, subscriber: (...args: any) => any) => void;
  unsubscribe: (event: string, subscriber: (...args: any) => any) => void;
};

export type Notification = {
  action_description?: string;
  action_icon?: IconName;
  avatar?: string;
  created_at: number;
  description?: string;
  event?: string;
  flag_color?: string;
  icon?: IconName;
  id: string;
  hasDetails?: boolean;
  idx?: number;
  is_read?: boolean;
  isDismissibleDuringProcessing?: boolean;
  message?: string;
  onActionClick?: (e: React.MouseEvent) => void;
  onDismissClick?: (callback?: () => void) => void;
  percent?: number;
  state?: 'PROCESSING' | 'ERROR' | 'DONE';
  stages?: WorkflowUpdatedStage[];
  text?: string;
  title?: string;
  type?: string;
  uid?: string;
};

export interface Metric {
  value: number | null;
  displayValue: string;
  kind: MetricKind;
  __typename?: string;
}

export interface CurrencyMetric extends Metric {
  isoCurrencyCode: string;
  currencySymbol: string;
  kind: MetricKind.CURRENCY;
}

export type PaginationData = {
  count: number;
  currentPage: number;
  grandTotal: number;
  perPage: number;
  total: number;
  totalPages: number;
};

export type CompanyRole = 'SELLER' | 'BUYER';
export type UserRole = 'ADMIN' | 'MANAGER' | 'INTERN';

export enum ListingStatus {
  ACCEPTED = 'ACCEPTED',
  CLOSED = 'CLOSED',
  NAMED = 'NAMED',
  PROCESSING = 'PROCESSING',
  PUBLISHED = 'PUBLISHED',
  UNPUBLISHED = 'UNPUBLISHED',
  SENT = 'SENT',
}

export type GraphQLErrorType = {
  graphQLErrors: {
    extensions: {
      exception: {
        validationErrors: { message: string; property: string }[];
      };
    };
  }[];
};

export enum MetricKind {
  NUMBER = 'NUMBER',
  PERCENTAGE = 'PERCENTAGE',
  CURRENCY = 'CURRENCY',
}

export enum OfferStatus {
  ACCEPTED = 'ACCEPTED',
  CLOSED = 'CLOSED',
  DECLINED = 'DECLINED',
  DRAFT = 'DRAFT',
  COUNTERED = 'COUNTERED',
  PROCESSING = 'PROCESSING',
  EXPIRED = 'EXPIRED',
  DELETED = 'DELETED',
}

export enum NegotiationLinesheetSocketContextType {
  BulkQuantity = 'BulkQuantity',
  BulkRemove = 'BulkRemove',
  BulkAdd = 'BulkAdd',
  BulkPrice = 'BulkPrice',
  CategoryRollupAction = 'CategoryRollupAction',
  SingleQuantity = 'SingleQuantity',
  SinglePrice = 'SinglePrice',
  SingleMisc = 'SingleMisc',
  ClearDraft = 'ClearDraft',
  UpdateNotes = 'UpdateNotes',
  BulkMetrics = 'bulkMetrics',
}

export enum ProductType {
  Item = 'item',
  Casepack = 'casepack',
}

export enum PackageType {
  SingleItem = 'single-item',
  MultiItem = 'multi-item',
}

export enum ValidationRule {
  letter = 'letter',
  capital = 'capital',
  number = 'number',
  eight = 'eight',
}

export enum ValidationRuleState {
  Passed = 'Passed',
  Failed = 'Failed',
  None = 'None',
}
